import { useSuspenseQuery } from "@tanstack/react-query";
import {
  assertNever,
  ControlCodes,
  flaggedControls,
  getControlName,
  queries,
  useAuthConfig,
  ValidControlCode,
} from "@theprelude/core";
import { z } from "zod";
import { controlInstanceSchema } from "@/lib/validators";
import { DefaultOverlay } from "../default-overlay";
import { useFeature } from "../flag/flag";
import { AmazonEC2IntegrationForm } from "./forms/amazon-ec2-integration-form";
import { AmazonSSMIntegrationForm } from "./forms/amazon-ssm-integration-form";
import { AzureVMIntegrationForm } from "./forms/azure-vm-integration-form";
import { CrowdstrikeIntegrationForm } from "./forms/crowdstrike-integration-form";
import { DefenderIntegrationForm } from "./forms/defender-integration-form";
import { EntraIntegrationOverlay } from "./forms/entra-integration-form";
import { GithubIntegrationForm } from "./forms/github-integration-form";
import { GmailIntegrationForm } from "./forms/gmail-integration-form";
import { GoogleIdentityIntegrationForm } from "./forms/google-identity-integration-form";
import { IntuneIntegrationForm } from "./forms/intune-integration-form";
import { JamfIntegrationForm } from "./forms/jamf-integration-form";
import { M365IntegrationForm } from "./forms/m365-integration-form";
import { OktaIntegrationForm } from "./forms/okta-integration-form";
import { QualysIntegrationForm } from "./forms/qualys-integration-form";
import { Rapid7IntegrationForm } from "./forms/rapid7-integration-form";
import { S3IntegrationForm } from "./forms/s3-integration-form";
import { SentinelOneIntegrationForm } from "./forms/sentinel-one-integration-form";
import { ServiceNowIntegrationForm } from "./forms/servicenow-integration-form";
import { SplunkIntegrationForm } from "./forms/splunk-integration-form";
import { TenableIntegrationForm } from "./forms/tenable-integration-form";
import { VectrIntegrationForm } from "./forms/vectr-integration-form";

export const integrationViewSchema = z.enum(["form", "groups"]);
export type IntegrationOverlayView = z.infer<typeof integrationViewSchema>;

export const integrationSchema = z.tuple([
  z.literal("integration"),
  z.object({
    ...controlInstanceSchema.shape,
    instanceId: controlInstanceSchema.shape.instanceId.optional(),
    view: integrationViewSchema.default("form").optional(),
  }),
]);

type IntegrationSchema = z.infer<typeof integrationSchema>;

export const getIntegrationForm = (control: ValidControlCode) => {
  switch (control) {
    case ControlCodes.AWS_SSM: {
      return AmazonSSMIntegrationForm;
    }
    case ControlCodes.AZURE_VM: {
      return AzureVMIntegrationForm;
    }
    case ControlCodes.CROWDSTRIKE: {
      return CrowdstrikeIntegrationForm;
    }
    case ControlCodes.CROWDSTRIKE_IDENTITY: {
      return null;
    }
    case ControlCodes.DEFENDER:
    case ControlCodes.DEFENDER_DISCOVERY: {
      return DefenderIntegrationForm;
    }
    case ControlCodes.EC2: {
      return AmazonEC2IntegrationForm;
    }
    case ControlCodes.ENTRA: {
      return EntraIntegrationOverlay;
    }
    case ControlCodes.GITHUB: {
      return GithubIntegrationForm;
    }
    case ControlCodes.GMAIL: {
      return GmailIntegrationForm;
    }
    case ControlCodes.GOOGLE_IDENTITY: {
      return GoogleIdentityIntegrationForm;
    }
    case ControlCodes.INTUNE:
    case ControlCodes.INTUNE_HOST_FIREWALL: {
      return IntuneIntegrationForm;
    }
    case ControlCodes.JAMF: {
      return JamfIntegrationForm;
    }
    case ControlCodes.M365: {
      return M365IntegrationForm;
    }
    case ControlCodes.OKTA: {
      return OktaIntegrationForm;
    }
    case ControlCodes.S3: {
      return S3IntegrationForm;
    }
    case ControlCodes.SENTINELONE: {
      return SentinelOneIntegrationForm;
    }
    case ControlCodes.SERVICENOW: {
      return ServiceNowIntegrationForm;
    }
    case ControlCodes.SPLUNK: {
      return SplunkIntegrationForm;
    }
    case ControlCodes.TENABLE:
    case ControlCodes.TENABLE_DISCOVERY: {
      return TenableIntegrationForm;
    }
    case ControlCodes.QUALYS:
    case ControlCodes.QUALYS_DISCOVERY: {
      return QualysIntegrationForm;
    }
    case ControlCodes.RAPID7:
    case ControlCodes.RAPID7_DISCOVERY: {
      return Rapid7IntegrationForm;
    }
    case ControlCodes.VECTR: {
      return VectrIntegrationForm;
    }
    default: {
      return assertNever(control);
    }
  }
};

export function IntegrationOverlay({
  instanceId,
  view = "form",
  control,
}: IntegrationSchema[1]) {
  const flag = flaggedControls.find((f) => f.control === control)?.flag;

  const flags = flag === undefined ? [] : [flag];
  const isFlagEnabled = useFeature(...flags);

  if (flag !== undefined && !isFlagEnabled) {
    return null;
  }

  const IntegrationForm = getIntegrationForm(control);

  if (!IntegrationForm) {
    return null;
  }

  return (
    <DefaultOverlay title={getControlName(control)}>
      {instanceId ? (
        <IntegrationInstanceForm
          instanceId={instanceId}
          view={view}
          form={IntegrationForm}
        />
      ) : (
        <IntegrationForm view={view} />
      )}
    </DefaultOverlay>
  );
}

const IntegrationInstanceForm = ({
  instanceId,
  view,
  form: IntegrationForm,
}: {
  instanceId: string;
  view: IntegrationOverlayView;
  form: NonNullable<ReturnType<typeof getIntegrationForm>>;
}) => {
  const authConfig = useAuthConfig();
  const { data: instance } = useSuspenseQuery({
    ...queries.iam.getAccount(authConfig),
    select: ({ controls }) =>
      controls.find((c) => c.instance_id === instanceId),
  });

  return <IntegrationForm instance={instance} view={view} />;
};
